<script lang="ts">
    import { onMount, getContext } from "svelte";
    import moment from "moment";
    import QrCode from "svelte-qrcode";
    import { link } from "svelte-navigator";
    import { api, loader, swClient, toast, user } from "../stores";
    import type {
        Campaign,
        Operation,
        Order,
        UpdateCampaignIn,
    } from "../model";
    import ConfirmPopup from "../modals/ConfirmPopup.svelte";
    import OperationAddModal from "../modals/OperationAddModal.svelte";
    import OperationDetailModal from "../modals/OperationDetailModal.svelte";
    import { Types } from "../model";
    import SearchFilterComponent from "../admin/components/SearchFilterComponent.svelte";
    import QrcodeModal from "../modals/QrcodeModal.svelte";

    let campaignsDiv;
    let last_scroll = 0;
    let showedItems = 15;
    let operations: Operation[] = [];
    let allOperations: Operation[];

    const { open } = getContext("simple-modal");

    onMount(async () => {
        getCampaigns();
    });

    function getCampaigns() {
        const processId = $loader.show("Chargement des opérations");
        operations = [];
        $swClient.getCampaigns().then(
            async (res: Campaign[]) => {
                let ops_promises = res.map((c) => formatOperation(c));
                Promise.allSettled(ops_promises).then((ops) => {
                    let temp = [];
                    ops.forEach((result) => {
                        temp.push(result?.value);
                    });
                    allOperations = temp;
                    $loader.hide(processId);
                });
            },
            (err) => {
                allOperations = [];
                console.error(err);
                $loader.hide(processId);
            }
        );
    }

    async function getCampaignShopId(operation: Operation): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            $swClient
                .getOrdersByCampaign(operation.uid)
                .then(async (res) => {
                    if (res.length > 0) {
                        $swClient
                            .getShop(res[0].shop_uid)
                            .then((res) => {
                                resolve(res.code);
                            })
                            .catch((err) => {
                                reject(err);
                            });
                    } else {
                        reject("Shop not found");
                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    async function formatOperation(item): Promise<Operation> {
        return new Promise((resolve, reject) => {
            let campaign: Operation = {
                uid: item.uid,
                name: item.name,
                type: item.type.name,
                geo_area: item.geo_area,
                is_paf: false,
                show_paf: true,
                campaign: item,
            };
            for (let j = 0; j < item.attrs.length; j++) {
                const attr = item.attrs[j];
                if (attr.key === Types.campaign.attrs.code) {
                    campaign.code = attr.value;
                }
                if (attr.key === Types.campaign.attrs.is_paf) {
                    campaign.is_paf =
                        attr.value == "true" || attr.value == true;
                }
                if (attr.key === Types.campaign.attrs.show_paf) {
                    campaign.show_paf =
                        attr.value == "true" || attr.value == true;
                }
            }
            for (let j = 0; j < item.milestones.length; j++) {
                const milestone = item.milestones[j];
                if (milestone.name === Types.campaign.milestones.start_date) {
                    campaign.startDate = milestone.date;
                }
                if (milestone.name === Types.campaign.milestones.end_date) {
                    campaign.endDate = milestone.date;
                }
                if (milestone.name === Types.campaign.milestones.display_date) {
                    campaign.displayDate = milestone.date;
                }
            }
            getCampaignShopId(campaign).then(
                (res) => {
                    campaign.link = window.location.origin + "/catalog/" + campaign.code + "/" + res;
                    resolve(campaign);
                },
                (err) => {
                    resolve(campaign);
                }
            );
        });
    }

    function csvToArray(str, delimiter = ";") {
        // make header
        const strHeader =
            "operationCode;shopCode;startDate;endDate;displayDate;version";
        const headers = strHeader.split(delimiter);

        // use split to create an array of each csv value row
        const rows = str.split("\n");

        // Map the rows
        // split values from each row into an array
        // use headers.reduce to create an object
        // object properties derived from headers:values
        // the object passed as an element of the array
        const arr = rows.map(function (row) {
            const values = row.split(delimiter);
            const el = headers.reduce(function (object, header, index) {
                object[header] = values[index]
                    ? values[index].trim()
                    : values[index];
                return object;
            }, {});
            return el;
        });

        // return the array
        return arr;
    }

    async function readMFile(mFile: File): Promise<string> {
        return new Promise<string>((res) => {
            const reader = new FileReader();
            reader.onload = function (ev) {
                res(ev.target.result);
            };
            reader.readAsText(mFile);
        });
    }

    async function analyseMFile(mContentStr: string): Promise<string[]> {
        return new Promise<string[]>((res) => {
            let datesError: string[] = [];
            // const mContentStr = ev.target.result;
            const datas = csvToArray(mContentStr);
            // console.log(datas)
            datas.forEach((data, i) => {
                const displayDateMoment = moment(
                    data.displayDate,
                    "DD/MM/YYYY"
                );
                const displayDate = displayDateMoment.toDate();
                const startDateMoment = moment(data.startDate, "DD/MM/YYYY");
                const startDate = startDateMoment.toDate();
                const endDateMoment = moment(data.endDate, "DD/MM/YYYY");
                const endDate = endDateMoment.toDate();
                if (displayDate.getTime() > startDate.getTime()) {
                    datesError.push(
                        "- La date d'affichage (" +
                            displayDate.toLocaleString() +
                            ") ne peut être supérieur à la date du début (" +
                            startDate.toLocaleString() +
                            ") pour le magasin " +
                            data.shopCode
                    );
                } else if (startDate.getTime() > endDate.getTime()) {
                    datesError.push(
                        "- La date du début (" +
                            startDate.toLocaleString() +
                            ") ne peut être supérieur à la date de fin (" +
                            endDate.toLocaleString() +
                            ") pour le magasin " +
                            data.shopCode
                    );
                }
                if (datas.length - 1 === i) {
                    res(datesError);
                }
            });
        });
    }

    async function postMFile(mFileContentStr: string, campaign_uid: string) {
        const loaderId = $loader.show(
            "Votre fichier _m est en cours d'import, merci de patienter quelques instants, l'opération peut prendre plusieurs minutes..."
        );
        const operation = await $swClient.getCampaign(campaign_uid);
        const operationCode = operation.attrs.find(
            (attr) => attr.key === "code"
        )?.value;
        return $api.postOrderImport(campaign_uid, mFileContentStr, $swClient.token)
            .then(async (res) => {
                $loader.hide(loaderId);
                // suggest seeing the report
                if (res) {
                    const onOkay = async () => {
                        res.blob().then((b) => {
                            let a = document.createElement("a");
                            a.href = URL.createObjectURL(b);
                            a.setAttribute(
                                "download",
                                "log_import_m_" + campaign_uid
                            );
                            a.click();
                        });
                    };
                    console.log(res)
                    // // open the modal
                    // open(
                    //     ConfirmPopup,
                    //     {
                    //         onOkay,
                    //         title: "L'import " + campaign_uid + " est terminé",
                    //         content:
                    //             "Souhaitez-vous avoir le rapport d'exécution ?",
                    //     },
                    //     {
                    //         closeOnOuterClick: false,
                    //         closeOnEsc: false,
                    //         closeButton: false,
                    //     }
                    // );
                } else {
                    $toast.error(
                        "Une erreur s'est produite, l'import ne s'est pas exécuté correctement.",
                        10000
                    );
                    console.error("Une erreur s'est produite, l'import ne s'est pas exécuté correctement.");
                }
            })
            .catch((e) => {
                $loader.hide(loaderId);
                $toast.error(
                    "Une erreur s'est produite, l'import ne s'est pas exécuté correctement.",
                    10000
                );
                console.error(e);
            });
    }

    async function importOrderFile(e, campaign_uid) {
        const mFile = e.target.files[0];
        // read mFile
        let mFileContentStr = await readMFile(mFile);
        // check dates in mFile
        let datesError = await analyseMFile(mFileContentStr);
        // if mFile has error in dates
        if (datesError.length > 0) {
            // refactor array with html
            datesError = datesError.map((e) => {
                return "<span style='color: red'>" + e + "</span><br>";
            });
            datesError.push(
                "<br><h2>Souhaitez-vous tout de même importer ce fichier ?</h2>"
            );
            // open the confirmation modal
            const onOkay = async () => {
                await postMFile(mFileContentStr, campaign_uid);
            };
            open(
                ConfirmPopup,
                {
                    onOkay,
                    title: "Veuillez vérifier les dates suivantes",
                    content: datesError.join(""),
                },
                {
                    closeOnOuterClick: false,
                    closeOnEsc: false,
                    closeButton: false,
                }
            );
        } else {
            await postMFile(mFileContentStr, campaign_uid);
        }
    }

    function onAddCampaign() {
        open(
            OperationAddModal,
            {},
            {},
            {
                onClose: () => {
                    getCampaigns();
                },
            }
        );
    }

    function onDetailsCampaign(operation: Campaign) {
        open(
            OperationDetailModal,
            { operation },
            {},
            {
                onClose: () => {
                    getCampaigns();
                },
            }
        );
    }

    function openQrCodeModal(link) {
        open(QrcodeModal, { url: link });
    }

    function openBOD(operation: Campaign) {
        let url = operation.code.indexOf("SAV") === 0 ? "https://nanotera.bigone.digital/produit/saveurs-ditalie/" : "https://nanotera.bigone.digital/produit/brico-deco/";
        url = operation.code.indexOf("AD") === 0 ? "https://nanotera.bigone.digital/produit/les-indispensables-de-lhiver/" : url;
        url = operation.code.indexOf("POU") === 0 ? "https://sudemo.bigone.digital/operation/votre-maison-va-vous-adorer/" : url;
        window.open(url, "_blank");
    }

    async function wheelEvent(event) {
        if (event.deltaY > 0) {
            showedItems++;
        }
    }

    async function handle_scroll(event) {
        if (campaignsDiv.scrollTop > last_scroll) {
            showedItems++;
        }
        last_scroll = campaignsDiv.scrollTop;
    }
</script>

<div
    class="wrapper"
    bind:this={campaignsDiv}
    on:wheel={wheelEvent}
    on:scroll={handle_scroll}
>
    <div class="sticky-menu p-2">
        <div id="header">
            <div>
                <button class="button is-dark bg-dark-black" on:click={onAddCampaign}>
                    <span>Créer une opération</span>
                </button>
            </div>
        </div>
        {#if allOperations}
            <SearchFilterComponent
                listToFilter={allOperations}
                bind:filteredList={operations}
            />
        {/if}
        {#if operations.length > 0}
            <div class="campaign-line columns header">
                <div class="campaign-case column">Opération</div>
                <div class="campaign-case column">Dates</div>
                <div class="campaign-case column">Fichier magasin</div>
                <div class="campaign-case column" style="text-align: center;">
                    E-catalogue
                </div>
                <div class="campaign-case column" style="text-align: center;">
                    Espace marque
                </div>
                <div class="campaign-case column" style="text-align: center;">
                    Détails
                </div>
                <div class="campaign-case column" style="text-align: center;">
                    Médiatisation
                </div>
            </div>
        {/if}
    </div>
    {#if operations.length > 0}
        {#each operations as operation, i}
            {#if i < showedItems}
                <div
                    class="campaign-line campaign-item columns {i % 2 === 0
                        ? 'bg-white'
                        : ''}"
                >
                    <div class="campaign-case column">
                        {operation.code} - {operation.name}
                    </div>
                    <div class="campaign-case column">
                        Du {moment(operation.startDate).format("DD/MM/YYYY")}
                        <br />
                        Au {moment(operation.endDate).format("DD/MM/YYYY")}
                    </div>
                    <div class="campaign-case column">
                        <label class="button is-dark btn-file">
                            <input
                                type="file"
                                accept=".csv"
                                on:change={(e) => {
                                    importOrderFile(e, operation.uid);
                                }}
                            />
                            Import magasin
                        </label>
                    </div>
                    <div class="campaign-case column link">
                        {#if operation.link}
                            <a class="txt-deco" href={operation.link} target="_blank">
                                Voir l'e-catalogue
                            </a>
                            <div class="qrcode" on:click={() => {openQrCodeModal(operation.link);}}>
                                <QrCode value={operation.link} />
                            </div>
                        {/if}
                    </div>
                    <div class="campaign-case column link">
                        <a
                            href="admin/brand-dashboard/{operation.code}"
                            use:link>Espace marque</a
                        >
                    </div>
                    <div
                        class="campaign-case column"
                        style="text-align: center;"
                    >
                        <button
                            class="button btn-elpev"
                            on:click={() => {
                                onDetailsCampaign(operation);
                            }}
                        >
                            <i class="fas fa-info" />
                        </button>
                    </div>
                    <div
                        class="campaign-case column"
                        style="text-align: center;"
                    >
                        <button
                            class="button big-one"
                            on:click={() => {
                                openBOD(operation);
                            }}
                        >
                            <span class="icon">
                                <i class="bigone" />
                            </span>
                        </button>
                    </div>
                </div>
            {/if}
        {/each}
    {:else}
        <h1>Aucune campagne disponible</h1>
    {/if}
</div>

<style lang="scss">
    @import "../styles/variables.scss";

    .big-one {
        background-color: black;
    }
    #header {
        width: 100%;
        display: flex;
        justify-content: right;
        margin-bottom: 10px;
    }

    .header {
        .campaign-case {
            color: black;
            font-weight: 900 !important;
            font-family: "Gilmer Bold" !important;
        }
    }

    .campaign-line {
        background-color: $beige;
        border-radius: 12px;
        .link {
            text-align: center;
            display: inline-block;
            .qrcode {
                height: 30px;
                width: 30px;
                margin: auto;
                margin-top: 2px;
            }
        }

        &.bg-white {
            background-color: white;
        }

        &:not(.header) {
            &:hover {
                filter: drop-shadow(0px 2px 8px $light-lavender);
                
            }
        }
    }

    .campaign-item{
        border: 1px solid $grey-light;
    }

    .btn-file {
        background-color: $dark-black;
        border-radius: 10px;
        input[type="file"] {
            display: none;
        }
    }

    .txt-deco {
        text-decoration: underline;
    }

    .sticky-menu {
        top: 0px;
        position: sticky;
        top: -1.25rem;
        margin-left: -0.75rem;
        margin-right: -0.75rem;
        margin-bottom: 24px;
        padding-top: 10px;
        z-index: 999;
    }

    .columns {
        margin-left: 0;
        margin-right: 0;
    }
</style>
